import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ArchiveAuditReportResponse,
  ArchiveDocumentCreatePayload,
  ArchiveDocumentsResponse
} from '@type/archive.types'
import { AXIOS_INSTANCE } from '../api'

type Props = {
  page?: number
  limit?: number
  query?: string
  sort_by?: string
  sort_dir?: string
  archive_folder_id?: string
  document_type?: string
  file_number?: string
  doctor_id?: string
  patient_id?: string
  specialty?: string
  enabled?: boolean
  auditReportEnabled?: boolean
}

export const useArchiveDoc = (args: Props = {}) => {
  const {
    page = 1,
    limit = 20,
    query,
    sort_by,
    sort_dir,
    archive_folder_id,
    document_type,
    file_number,
    doctor_id,
    patient_id,
    specialty,
    enabled = true,
    auditReportEnabled = false
  } = args

  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    enabled,
    queryKey: [
      'archive-doc',
      page,
      limit,
      query,
      sort_by,
      sort_dir,
      archive_folder_id,
      document_type,
      file_number,
      doctor_id,
      patient_id,
      specialty
    ],
    queryFn: async () => {
      const { data: response } = await AXIOS_INSTANCE.get<ArchiveDocumentsResponse>('/archive_documents', {
        params: {
          'page[number]': page,
          'page[size]': limit,
          ...(query && { query }),
          ...(sort_by && { sort_by }),
          ...(sort_dir && { sort_dir }),
          ...(archive_folder_id && { archive_folder_id }),
          ...(document_type && { document_type }),
          ...(file_number && { file_number }),
          ...(doctor_id && { doctor_id }),
          ...(patient_id && { patient_id }),
          ...(specialty && { specialty })
        }
      })
      return response
    }
  })

  const {
    data: auditReportResponse,
    isLoading: auditReportIsLoading,
    error: auditReportError
  } = useQuery({
    enabled: auditReportEnabled,
    queryKey: ['archive-doc-audit-report'],
    queryFn: async () => {
      const { data: response } = await AXIOS_INSTANCE.get<ArchiveAuditReportResponse>('/archive_documents/audit_report')
      return response
    }
  })

  const { mutateAsync: createArchiveDocument, isPending: isCreating } = useMutation({
    mutationFn: async (payload: ArchiveDocumentCreatePayload) => {
      const { data: response } = await AXIOS_INSTANCE.post('/archive_documents', payload, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['archive-doc'] })
      queryClient.invalidateQueries({ queryKey: ['archive-doc-audit-report'] })
      queryClient.invalidateQueries({ queryKey: ['archives'] })
    }
  })

  return {
    documents: data?.data,
    included: data?.included,
    pagination: data?.meta?.pagination,
    auditReport: auditReportResponse?.data,
    auditReportIsLoading,
    auditReportError,
    isLoading,
    isCreating,
    createArchiveDocument,
    error
  }
}
